import { TriangleAlert } from "lucide-react";
/**
 * "Your world is going up to the cloud": an endless little animation for "Share world" (blocks rise from a small
 * island into a cloud), with a progress bar that always moves, so a long upload never looks frozen.
 *
 *   <CloudUpload>   the big one, under World files in the Plan together sheet
 *   <UploadPill>    a small pill next to the menubar's Plan together pill while the sheet is closed
 *                   (+ "World shared" / "Upload failed" after); rendered by CollabButton (Share.tsx)
 *
 * Plain inline SVG + CSS keyframes (styles.css `cu-*`, redesign in share.css), flat colours only — nothing from the
 * game's textures. With "reduce motion" the scene stands still and only the numbers and the bar change.
 */
import { useEffect, useRef, useState } from "react";
import { useApp, type ShareStep } from "../state/store.ts";

const HEADLINE: Record<ShareStep, string> = {
  prepare: "Getting ready…",
  map: "Finishing the map first…",
  upload: "Uploading your world to the cloud",
  publish: "Almost done…",
};

/** a small isometric block (flat colours: top, left, right), centred on 0,0 */
function Cube({ s = 10, top, left, right }: { s?: number; top: string; left: string; right: string }) {
  const h = s / 2;
  return (
    <>
      <polygon points={`0,${-h} ${s},0 0,${h} ${-s},0`} fill={top} />
      <polygon points={`${-s},0 0,${h} 0,${h + s} ${-s},${s}`} fill={left} />
      <polygon points={`0,${h} ${s},0 ${s},${s} 0,${h + s}`} fill={right} />
    </>
  );
}

const GRASS = { top: "#7cc955", left: "#7a5236", right: "#654330" };
const STONE = { top: "#a9b2b7", left: "#838c91", right: "#6c7479" };
const PLANK = { top: "#d2a866", left: "#a98247", right: "#8f6c3a" };
const CLOUD = "M88 44a14 14 0 0 1 4-27a20 20 0 0 1 36-8a16 16 0 0 1 27 12a11.5 11.5 0 0 1-1 23z";

export function CloudScene({ className = "" }: { className?: string }) {
  return (
    <svg className={`cu-scene ${className}`} viewBox="0 0 240 112" role="img" aria-label="Uploading the world to the cloud">
      <defs>
        <radialGradient id="cu-glow-g">
          <stop offset="0" stopColor="#8fd96a" stopOpacity="0.45" />
          <stop offset="1" stopColor="#8fd96a" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="cu-cloud-g" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#f4f7f8" />
          <stop offset="1" stopColor="#cdd7dc" />
        </linearGradient>
      </defs>

      {/* data streams from the island up to the cloud */}
      <path className="cu-stream" d="M104 80 C 98 66, 106 56, 104 44" />
      <path className="cu-stream cu-stream-2" d="M136 80 C 142 66, 134 56, 137 44" />

      {/* the blocks on their way up (each loops; offset so one arrives every 0.8 s) */}
      <g transform="translate(112 76)"><g className="cu-rise" style={{ "--dx": "4px", "--still": "-6px", animationDelay: "0s" } as React.CSSProperties}><Cube {...GRASS} /></g></g>
      <g transform="translate(128 76)"><g className="cu-rise" style={{ "--dx": "-3px", "--still": "-18px", animationDelay: "-0.8s" } as React.CSSProperties}><Cube {...STONE} /></g></g>
      <g transform="translate(120 78)"><g className="cu-rise" style={{ "--dx": "1px", "--still": "-28px", animationDelay: "-1.6s" } as React.CSSProperties}><Cube {...PLANK} /></g></g>

      {/* the cloud (glows and puffs up a little whenever a block arrives) */}
      <ellipse className="cu-glow" cx="121" cy="28" rx="62" ry="30" fill="url(#cu-glow-g)" />
      <g className="cu-puff">
        <path d={CLOUD} fill="url(#cu-cloud-g)" />
        <path d="M100 38h44" stroke="#b9c5cb" strokeWidth="2" strokeLinecap="round" opacity="0.55" />
      </g>

      {/* the little world */}
      <g transform="translate(120 88)">
        <polygon points="-30,0 0,-12 30,0 0,12" fill="#6fbf4a" />
        <polygon points="-30,0 0,12 0,22 -30,10" fill="#7a5236" />
        <polygon points="0,12 30,0 30,10 0,22" fill="#654330" />
        <polygon points="-30,0 0,12 0,14 -30,2" fill="#5aa33a" />
        <polygon points="0,12 30,0 30,2 0,14" fill="#4c8f30" />
        {/* a tiny tree */}
        <rect x="-19" y="-12" width="3" height="7" fill="#6b4a2e" />
        <g transform="translate(-17.5 -15)"><Cube s={6} top="#4f9a35" left="#3d7f28" right="#336d21" /></g>
      </g>
    </svg>
  );
}

function Bar({ frac }: { frac: number | null | undefined }) {
  const known = typeof frac === "number";
  return (
    <div className={`cu-bar ${known ? "" : "indeterminate"}`} role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={known ? Math.round(frac * 100) : undefined}>
      <i style={known ? { width: `${Math.max(3, Math.min(100, frac * 100))}%` } : undefined} />
    </div>
  );
}

/** the big one: scene, headline, what's happening, progress */
export function CloudUpload() {
  const share = useApp((s) => s.share);
  if (share.state !== "uploading") return null;
  const step = share.step ?? "prepare";
  const pct = typeof share.frac === "number" ? `${Math.round(share.frac * 100)}%` : "";
  return (
    <div className="cloud-upload" aria-live="polite">
      <CloudScene />
      <div className="cu-head"><b>{HEADLINE[step]}</b><span className="cu-pct">{pct}</span></div>
      <Bar frac={share.frac} />
      {share.msg && <span className="cu-msg">{share.msg}</span>}
      <span className="cu-note">You can keep planning while this runs.</span>
    </div>
  );
}

/** small cloud with a block going up, for the top bar */
function MiniCloud({ done }: { done?: boolean }) {
  return (
    <svg className="cu-mini" viewBox="72 0 100 56" width="26" height="15" aria-hidden="true">
      <path d={CLOUD} fill="#e7ecef" />
      {done
        ? <path d="M106 28l9 8l17-17" fill="none" stroke="#3b7a22" strokeWidth="7" strokeLinecap="round" strokeLinejoin="round" />
        : <g transform="translate(121 44)"><g className="cu-rise cu-rise-mini"><Cube s={9} {...GRASS} /></g></g>}
    </svg>
  );
}

/** menubar: what the upload is doing while the sheet is closed; a short "World shared" / "Upload failed" after it */
export function UploadPill({ onOpen }: { onOpen: () => void }) {
  const share = useApp((s) => s.share);
  const [after, setAfter] = useState<"shared" | "error" | null>(null);
  const prev = useRef(share.state);
  useEffect(() => {
    const was = prev.current;
    prev.current = share.state;
    if (was === "uploading" && share.state === "shared") {
      setAfter("shared");
      const t = setTimeout(() => setAfter(null), 5000);
      return () => clearTimeout(t);
    }
    if (was === "uploading" && share.state === "error") setAfter("error");
    else if (share.state !== "error") setAfter((a) => (a === "error" ? null : a));
  }, [share.state]);

  if (share.state === "uploading") {
    const pct = typeof share.frac === "number" && share.step === "upload" ? ` ${Math.round(share.frac * 100)}%` : "…";
    return (
      <button className="btn btn-pill upload-pill" onClick={onOpen} title={share.msg ?? "Uploading the world"}>
        <MiniCloud /><span className="tabular">Uploading{pct}</span>
      </button>
    );
  }
  if (after === "shared") return <button className="btn btn-pill upload-pill done" onClick={onOpen}><MiniCloud done />World shared</button>;
  if (after === "error") return <button className="btn btn-pill upload-pill failed" onClick={onOpen} title={share.msg}><TriangleAlert size={14} aria-hidden="true" />Upload failed</button>;
  return null;
}
